import { z } from 'zod';

import { isoDaySchema } from './clinical-common.ts';

// Terminology: code systems, versions, concepts, search and the capture contract
// (docs/plans/phase1-terminology-and-import.md §2 and §3).
//
// Search text can be clinical, so search and lookup are POST bodies, never query strings.

/** A code system's short, stable key, such as `icd11_mms`. */
export const codeSystemKeySchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]{1,39}$/, 'Not a code system key');

export const CODE_MATCHES = ['case_insensitive', 'exact'] as const;
export const CODE_SYSTEM_VERSION_STATUSES = [
  'importing',
  'ready',
  'active',
  'retired',
  'failed',
] as const;
export const CONCEPT_STATUSES = ['active', 'inactive'] as const;
export const MATCH_KINDS = ['code', 'text', 'similar'] as const;
export const CODING_SOURCES = ['local', 'national_service'] as const;

/**
 * What any clinical or catalogue record stores when it captures a code (domain rule 9):
 * the system URI, the version, the code and the display as they were at capture. Tables
 * store these as four columns, with no foreign key to `concepts`, so a captured code
 * outlives the version it came from.
 */
export const terminologyCodingSchema = z.object({
  system: z.string().trim().min(1).max(200),
  version: z.string().trim().min(1).max(64),
  code: z.string().trim().min(1).max(64),
  display: z.string().trim().min(1).max(500),
  source: z.enum(CODING_SOURCES),
});

export type TerminologyCoding = z.infer<typeof terminologyCodingSchema>;

export const TERMINOLOGY_SEARCH_LIMIT = 20;

export const terminologySearchRequestSchema = z.object({
  systems: z.array(codeSystemKeySchema).min(1).max(4),
  query: z.string().trim().min(2).max(100),
  limit: z.number().int().min(1).max(TERMINOLOGY_SEARCH_LIMIT).default(TERMINOLOGY_SEARCH_LIMIT),
  includeNational: z.boolean().default(true),
});

export type TerminologySearchRequest = z.infer<typeof terminologySearchRequestSchema>;

export const conceptMatchSchema = z.object({
  /** Null for results from the national terminology service. */
  conceptId: z.uuid().nullable(),
  code: z.string(),
  display: z.string(),
  /** The synonym or index term that matched, when it isn't the display. */
  matchedTerm: z.string().nullable(),
  matchKind: z.enum(MATCH_KINDS),
  system: z.string(),
  version: z.string(),
  /** The parent's display (for ICD-11, the block or chapter), if known. */
  parentDisplay: z.string().nullable(),
});

export type ConceptMatchView = z.infer<typeof conceptMatchSchema>;

export const NATIONAL_SEARCH_STATES = [
  /** Local results were found, so the national service wasn't asked. */
  'not_needed',
  'results',
  'unavailable',
  /** No adapter is configured for this deployment. */
  'not_configured',
  /** The request asked for local results only. */
  'not_requested',
] as const;

export const terminologySearchResponseSchema = z.object({
  local: z.array(conceptMatchSchema),
  national: z.object({
    state: z.enum(NATIONAL_SEARCH_STATES),
    items: z.array(conceptMatchSchema),
  }),
});

export type TerminologySearchResponse = z.infer<typeof terminologySearchResponseSchema>;

export const terminologyLookupRequestSchema = z.object({
  system: z.string().trim().min(1).max(200),
  version: z.string().trim().min(1).max(64),
  code: z.string().trim().min(1).max(64),
});

export const conceptSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  display: z.string(),
  status: z.enum(CONCEPT_STATUSES),
  selectable: z.boolean(),
  parentCode: z.string().nullable(),
  system: z.string(),
  version: z.string(),
  versionStatus: z.enum(CODE_SYSTEM_VERSION_STATUSES),
  synonyms: z.array(z.string()),
});

export type ConceptView = z.infer<typeof conceptSchema>;

export const codeSystemVersionSchema = z.object({
  id: z.uuid(),
  version: z.string(),
  releaseDate: isoDaySchema.nullable(),
  status: z.enum(CODE_SYSTEM_VERSION_STATUSES),
  conceptCount: z.number().int(),
  designationCount: z.number().int(),
  activatedAt: z.iso.datetime().nullable(),
  retiredAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});

export type CodeSystemVersionView = z.infer<typeof codeSystemVersionSchema>;

export const codeSystemSchema = z.object({
  id: z.uuid(),
  key: codeSystemKeySchema,
  uri: z.string(),
  name: z.string(),
  publisher: z.string(),
  licenceNotice: z.string(),
  codeMatch: z.enum(CODE_MATCHES),
  /** Reference data is shared by every tenant and read-only for them (ADR 0009). */
  owner: z.enum(['reference', 'tenant']),
  activeVersion: codeSystemVersionSchema.nullable(),
});

export type CodeSystemView = z.infer<typeof codeSystemSchema>;

export const codeSystemListSchema = z.object({
  items: z.array(codeSystemSchema),
  /** Whether this tenant publishes the reference data, so may import and activate. */
  isReferencePublisher: z.boolean(),
});

export const codeSystemVersionListSchema = z.object({
  codeSystem: codeSystemSchema,
  items: z.array(codeSystemVersionSchema),
});

export const activateVersionBodySchema = z.object({
  reason: z.string().trim().min(5).max(500),
});

/** A national-service result a person picked, counted per tenant (plan §2.5). */
export const remoteSelectionBodySchema = terminologyCodingSchema.extend({
  source: z.literal('national_service'),
});
